<template>
  <div class="break-glass-page">
    <header class="page-header">
      <h1>Break-Glass</h1>
      <p>Temporarily grant chart access and clinical viewing privileges only.</p>
    </header>

    <div class="toolbar">
      <button class="btn btn-secondary" type="button" @click="loadData" :disabled="loading">
        {{ loading ? "Refreshing..." : "Refresh" }}
      </button>
    </div>

    <div v-if="errorMessage" class="status status--error">{{ errorMessage }}</div>
    <div v-if="successMessage" class="status status--ok">{{ successMessage }}</div>

    <div class="panel">
      <table class="users-table">
        <thead>
          <tr>
            <th>Caregiver</th>
            <th>Username</th>
            <th>Type</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="u in caregiverRows" :key="u.unique_ID">
            <td>{{ displayName(u) }}</td>
            <td>{{ u.username || "-" }}</td>
            <td>{{ u.account_type || "-" }}</td>
            <td>
              <span v-if="u.breakGlassActive" class="tag tag--active">Active</span>
              <span v-else class="tag tag--inactive">Inactive</span>
            </td>
            <td>
              <button
                v-if="!u.breakGlassActive"
                class="btn btn-danger"
                type="button"
                :disabled="loadingByUser[u.unique_ID]"
                @click="startBreakGlass(u.unique_ID)"
              >
                Activate
              </button>
              <button
                v-else
                class="btn btn-primary"
                type="button"
                :disabled="loadingByUser[u.unique_ID]"
                @click="endBreakGlass(u.unique_ID)"
              >
                Revoke
              </button>
            </td>
          </tr>
          <tr v-if="!loading && caregiverRows.length === 0">
            <td colspan="5" class="empty">No caregiver users found.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { mapGetters } from "vuex";
import { CURR_API } from "@/constants/apis";
import { usePrivilegesStore } from "@/stores/privileges";
import { useSessionSelfStore } from "@/stores/sessionSelf";
import { sessionActorFrom } from "@/utils/sessionIdentity";
import { PRIVILEGES } from "@/constants/privileges";
import { syncPrivilegesAfterSessionChange } from "@/utils/privilegesSessionSync";

export default {
  name: "BreakGlassPage",
  data() {
    return {
      loading: false,
      users: [],
      statusesByUser: {},
      loadingByUser: {},
      errorMessage: "",
      successMessage: "",
    };
  },
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    accountNumber() {
      return sessionActorFrom(this.allData, useSessionSelfStore().profile).accountnumber;
    },
    actorUniqueId() {
      return sessionActorFrom(this.allData, useSessionSelfStore().profile).unique_ID;
    },
    canAdjustPermissions() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.ADJUST_PERMISSIONS);
    },
    caregiverRows() {
      return (this.users || [])
        .filter((u) => String(u.account_type || "").trim().toLowerCase() !== "patient")
        .map((u) => ({
          ...u,
          breakGlassActive: !!(this.statusesByUser[u.unique_ID] && this.statusesByUser[u.unique_ID].is_active),
        }))
        .sort((a, b) => this.displayName(a).localeCompare(this.displayName(b)));
    },
  },
  methods: {
    normalizePayload(data) {
      if (!data || typeof data !== "object") return {};
      if (Object.prototype.hasOwnProperty.call(data, "status") || Object.prototype.hasOwnProperty.call(data, "data")) {
        return data;
      }
      const keys = Object.keys(data);
      if (keys.length === 1 && data[keys[0]] && typeof data[keys[0]] === "object") {
        return data[keys[0]];
      }
      return data;
    },
    displayName(u) {
      const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      return name || u.username || u.unique_ID;
    },
    async loadUsers() {
      const body = { Account: { API_Meth: "GetAccountUsers", accountnumber: this.accountNumber } };
      const res = await axios.post(CURR_API + "/tswebhook.php", body, {
        headers: { "Content-Type": "application/json;" },
      });
      const payload = this.normalizePayload(res.data);
      const list = Array.isArray(payload.data) ? payload.data : [];
      this.users = list
        .map((u) => ({
          unique_ID: String(u.subaccountnumber || u.unique_ID || u.subAccountNumber || "").trim(),
          username: String(u.username || "").trim(),
          account_type: String(u.account_type || u.accountType || "").trim(),
          first_name: String(u.firstname || u.Firstname || u.user_Fname || "").trim(),
          last_name: String(u.lastname || u.Lastname || u.user_Lname || "").trim(),
        }))
        .filter((u) => u.unique_ID);
    },
    async loadStatuses() {
      const body = {
        Privileges: {
          API_Meth: "GetBreakGlassStatuses",
          accountnumber: this.accountNumber,
          unique_ID: this.actorUniqueId,
        },
      };
      const res = await axios.post(CURR_API + "/tswebhook.php", body, {
        headers: { "Content-Type": "application/json;" },
      });
      const payload = this.normalizePayload(res.data);
      const map = payload && payload.data && typeof payload.data === "object" ? payload.data : {};
      this.statusesByUser = map;
    },
    async loadData() {
      this.errorMessage = "";
      this.successMessage = "";
      if (!this.canAdjustPermissions) {
        this.errorMessage = "You do not have permission to manage break-glass.";
        return;
      }
      if (!this.accountNumber || !this.actorUniqueId) {
        this.errorMessage = "Unable to resolve account/user identity for this session.";
        return;
      }
      this.loading = true;
      try {
        await this.loadUsers();
        await this.loadStatuses();
      } catch (e) {
        this.errorMessage = "Failed to load break-glass data.";
      } finally {
        this.loading = false;
      }
    },
    async startBreakGlass(targetUniqueId) {
      this.successMessage = "";
      this.errorMessage = "";
      this.loadingByUser = { ...this.loadingByUser, [targetUniqueId]: true };
      try {
        const body = {
          Privileges: {
            API_Meth: "StartBreakGlass",
            accountnumber: this.accountNumber,
            unique_ID: this.actorUniqueId,
            actor_unique_ID: this.actorUniqueId,
            target_unique_ID: targetUniqueId,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", body, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizePayload(res.data);
        const ok = String(payload.status || "").toLowerCase().includes("success");
        if (!ok) {
          this.errorMessage = payload.message || "Failed to activate break-glass.";
          return;
        }
        this.successMessage = "Break-glass activated.";
        if (String(targetUniqueId) === String(this.actorUniqueId)) {
          syncPrivilegesAfterSessionChange();
        }
        await this.loadStatuses();
      } catch (e) {
        this.errorMessage = "Failed to activate break-glass.";
      } finally {
        this.loadingByUser = { ...this.loadingByUser, [targetUniqueId]: false };
      }
    },
    async endBreakGlass(targetUniqueId) {
      this.successMessage = "";
      this.errorMessage = "";
      this.loadingByUser = { ...this.loadingByUser, [targetUniqueId]: true };
      try {
        const body = {
          Privileges: {
            API_Meth: "EndBreakGlass",
            accountnumber: this.accountNumber,
            unique_ID: this.actorUniqueId,
            actor_unique_ID: this.actorUniqueId,
            target_unique_ID: targetUniqueId,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", body, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizePayload(res.data);
        const ok = String(payload.status || "").toLowerCase().includes("success");
        if (!ok) {
          this.errorMessage = payload.message || "Failed to revoke break-glass.";
          return;
        }
        this.successMessage = "Break-glass revoked.";
        if (String(targetUniqueId) === String(this.actorUniqueId)) {
          syncPrivilegesAfterSessionChange();
        }
        await this.loadStatuses();
      } catch (e) {
        this.errorMessage = "Failed to revoke break-glass.";
      } finally {
        this.loadingByUser = { ...this.loadingByUser, [targetUniqueId]: false };
      }
    },
  },
  /**
   * Warn before break-glass actions that can elevate access to patient data.
   * Proceed loads the page; Go back returns to Administration.
   */
  async mounted() {
    const result = await Swal.fire({
      icon: "warning",
      title: "Warning",
      text: "This page can grant privileges that expose patient data to users.",
      showCancelButton: true,
      confirmButtonText: "Proceed",
      cancelButtonText: "Go back",
      confirmButtonColor: "#4b9ca5",
      cancelButtonColor: "#6c757d",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
    if (result.isConfirmed) {
      this.loadData();
      return;
    }
    this.$router.push("/administration");
  },
};
</script>

<style scoped lang="scss">
.break-glass-page {
  padding: 1.25rem;
}
.page-header h1 {
  margin: 0;
}
.page-header p {
  margin: 0.35rem 0 1rem;
  color: #5b6472;
}
.toolbar {
  margin-bottom: 0.75rem;
}
.status {
  margin-bottom: 0.65rem;
  padding: 0.55rem 0.7rem;
  border-radius: 8px;
  font-weight: 600;
}
.status--error {
  background: #fee2e2;
  color: #991b1b;
}
.status--ok {
  background: #dcfce7;
  color: #166534;
}
.panel {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: auto;
}
.users-table {
  width: 100%;
  border-collapse: collapse;
}
.users-table th,
.users-table td {
  padding: 0.7rem;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
}
.tag {
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
}
.tag--active {
  background: #fef3c7;
  color: #92400e;
}
.tag--inactive {
  background: #e2e8f0;
  color: #334155;
}
.btn {
  border: none;
  border-radius: 8px;
  padding: 0.45rem 0.7rem;
  font-weight: 600;
}
.btn-primary {
  background: #0c60ff;
  color: #fff;
}
.btn-danger {
  background: #dc2626;
  color: #fff;
}
.btn-secondary {
  background: #e2e8f0;
  color: #1e293b;
}
.btn:disabled {
  opacity: 0.55;
}
.empty {
  color: #64748b;
  font-style: italic;
}
</style>
